import React, { useState } from 'react';
import clsx from 'clsx';
import {
  ButtonBase,
  Grid,
  Box,
  createStyles,
  makeStyles,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
  Theme,
} from '@material-ui/core';
import { Check, XCircle } from 'react-feather';

import {
  CoverageCatalog,
  Quotation,
  QuotationCoverage,
} from 'data-layer-cr/dist/models';
import {
  endorsementCoverages,
  fullDataForCode,
} from 'data-layer-cr/dist/functions/coverages';
import { uniqueId } from 'lodash';

import {
  colors,
  SubTitle1Text,
  H4Text,
  moneyFormat,
  DefaultButton,
  HeaderText,
  CaptionText,
} from 'design-system';

import AdditionalBenefits from './AdditionalBenefits';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
    },
    fakeRadio: {
      width: 24,
      height: 24,
      borderRadius: 12,
      border: `2px solid ${colors.bright_turquoise}`,
      marginRight: 10,
    },
    checkedRadio: {
      backgroundColor: colors.bright_turquoise,
    },
    buttonText: {
      color: colors.bright_turquoise,
      lineHeight: '19px',
      letterSpacing: '1.25px',
    },
    closeIcon: {
      position: 'absolute',
      top: 14,
      right: theme.spacing(2),
      zIndex: 1101,
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
      },
    },
    dialogue: {
      borderRadius: 10,
      [theme.breakpoints.down('md')]: {
        borderRadius: 0,
      },
    },
  }),
);

const { formatMoney } = moneyFormat;

type Props = {
  productEndorsements: QuotationCoverage[];
  catalogCoverages: CoverageCatalog[];
  currentValue?: string;
  isEditable: boolean;
  quotation: Quotation;
  additionalBenefits?: CoverageCatalog[];
};

const QuotationEndorsements = ({
  productEndorsements,
  catalogCoverages,
  currentValue,
  isEditable,
  quotation,
  additionalBenefits = [],
}: Props): JSX.Element => {
  const classes = useStyles();

  const [dialogue, toggleDialogue] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /** Retrieve only coverages with type E */
  const endorsementCoverageList = endorsementCoverages(catalogCoverages);

  const isSelected = (coverageId: string): boolean => {
    if (!isEditable) {
      return true;
    }

    if (currentValue === coverageId) {
      return true;
    }
    return false;
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SubTitle1Text>Beneficios de la póliza</SubTitle1Text>
      </Grid>
      <Grid item xs={12} data-testid="assa-movil-logo">
        <Grid container alignItems="center">
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            item
            className={clsx(classes.fakeRadio, classes.checkedRadio)}
          >
            <Check color={colors.white} size={16} />
          </Grid>
          <Grid item>
            <Box>
              <img src="svg/assa_movil.svg" alt="assa_movil" />
              <CaptionText>Asistencia vial exclusiva</CaptionText>
            </Box>
          </Grid>
        </Grid>
        {additionalBenefits.map((ab) => (
          <AdditionalBenefits key={ab.code} name={ab.name} />
        ))}
      </Grid>
      <Grid item container spacing={2} data-testid="container-list-endorsement">
        {productEndorsements.map((endorsement) => {
          /** Since we need the names, we have to retrieve it from catalog */
          const currentEndorsement = fullDataForCode(
            endorsementCoverageList,
            endorsement.coverageId,
          );

          /** In case pricing does not match with catalog. */
          if (!currentEndorsement) {
            return null;
          }

          return (
            <Grid container item xs={12} key={uniqueId()}>
              <ButtonBase className={classes.buttonContainer} disableRipple>
                <Grid container alignItems="center">
                  <Grid
                    data-testid={`check-${endorsement.coverageId}`}
                    container
                    alignItems="center"
                    justifyContent="center"
                    item
                    className={clsx(
                      classes.fakeRadio,
                      isSelected(endorsement.coverageId) &&
                        classes.checkedRadio,
                    )}
                  >
                    {isSelected(endorsement.coverageId) && (
                      <Check color={colors.white} size={16} />
                    )}
                  </Grid>
                  <Grid item>
                    <H4Text
                      style={{ textTransform: 'capitalize' }}
                      data-testid={`name-${endorsement.coverageId}`}
                    >
                      {currentEndorsement.name.toLowerCase()}
                    </H4Text>
                  </Grid>
                </Grid>
                <Grid item>
                  <SubTitle1Text
                    data-testid={`maxCost-${endorsement.coverageId}`}
                  >
                    {!currentEndorsement.maxCost
                      ? 'Gratis'
                      : formatMoney(
                          currentEndorsement.maxCost,
                          false,
                          quotation.quotationProfile.typeOfMoney,
                        )}
                  </SubTitle1Text>
                </Grid>
              </ButtonBase>
            </Grid>
          );
        })}
        {isEditable && currentValue && (
          <Grid item xs={12}>
            <DefaultButton
              data-testid="comparison-button"
              fullWidth
              variant="borderless"
              onClick={(): void => toggleDialogue(true)}
            >
              <HeaderText className={classes.buttonText}>
                Ver comparativo
              </HeaderText>
            </DefaultButton>
          </Grid>
        )}
      </Grid>
      {/** This section could be improved, it was copied from DynamicEndorsementCard */}
      {isEditable && currentValue && (
        <Dialog
          data-testid="dialogue"
          open={dialogue}
          onClose={(): void => toggleDialogue(false)}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          PaperProps={{
            className: classes.dialogue,
          }}
        >
          <IconButton
            data-testid="close-button"
            className={classes.closeIcon}
            onClick={(): void => toggleDialogue(false)}
          >
            <XCircle size={24} color={colors.white} />
          </IconButton>
        </Dialog>
      )}
    </Grid>
  );
};

export default QuotationEndorsements;
